import { useCallback, useEffect, useState } from 'react';

interface UseDragNumberProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export function useDragNumber({
  value,
  onChange,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  step = 1
}: UseDragNumberProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [startValue, setStartValue] = useState(0);
  const [virtualCursor, setVirtualCursor] = useState({ x: 0, y: 0 });
  const [, setTotalMovement] = useState(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setStartValue(value);
      setTotalMovement(0);
      setVirtualCursor({ x: e.clientX, y: e.clientY });

      // Request pointer lock
      e.currentTarget.requestPointerLock();

      // Hide the actual cursor and show virtual cursor
      document.body.style.cursor = 'none';
      window.getSelection()?.empty();
    },
    [value]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Accumulate total movement
      setTotalMovement((prev) => {
        const newTotal = prev + e.movementX;

        // Calculate new value based on movement
        // Using a sensitivity factor of 0.1 for smoother control
        const delta = Math.round(newTotal * 0.1);
        const newValue = startValue + delta * step;
        const clampedValue = Math.min(Math.max(newValue, min), max);

        onChange(clampedValue);
        return newTotal;
      });

      // Update virtual cursor position
      setVirtualCursor((prev) => {
        const newX = prev.x + e.movementX;
        const screenWidth = window.innerWidth;

        // Wrap around screen edges
        if (newX > screenWidth) {
          return { ...prev, x: 0 };
        } else if (newX < 0) {
          return { ...prev, x: screenWidth };
        }
        return { ...prev, x: newX };
      });

      window.getSelection()?.empty();
    },
    [isDragging, startValue, step, min, max, onChange]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Exit pointer lock
      document.exitPointerLock();
      document.body.style.cursor = 'default';
      setIsDragging(false);
    }
  }, [isDragging]);

  // Create and update virtual cursor element
  useEffect(() => {
    if (isDragging) {
      const cursor = document.createElement('div');
      cursor.id = 'virtual-cursor';
      cursor.style.cssText = `
        position: fixed;
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.5);
        border: 2px solid white;
        border-radius: 12px;
        pointer-events: none;
        z-index: 9999;
        transform: translate(-50%, -50%);
      `;
      document.body.appendChild(cursor);

      const updateCursor = () => {
        cursor.style.left = virtualCursor.x + 'px';
        cursor.style.top = virtualCursor.y + 'px';
      };
      updateCursor();

      return () => {
        document.body.removeChild(cursor);
      };
    }

    return;
  }, [isDragging, virtualCursor]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    isDragging,
    handleMouseDown
  };
}
